import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUsersApi } from "@/hooks/useUsersApi";
import { useBrandsApi } from "@/hooks/useBrandsApi";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import ErrorMessage from "@/components/orders/ErrorMessage";
import ProfileInfoSection from "./partials/ProfileInfoSection";
import PasswordSection from "./partials/PasswordSection";
import { PageHeader } from "@/components/PageHeader";

// Cache para almacenar los datos del usuario
const userDataCache = new Map<string, any>();

export default function MyProfile() {
  const { user, refreshUserData } = useAuth();
  const { getUserByEmail, clearUserCache } = useUsersApi();
  const { brands } = useBrandsApi();

  // Estados para los datos del usuario
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  // Cargar datos del usuario
  const fetchData = useCallback(async () => {
    if (!user?.email || hasAttemptedFetch) return;

    try {
      setLoading(true);
      setHasAttemptedFetch(true);

      // Verificar si tenemos los datos en caché
      if (userDataCache.has(user.email)) {
        setUserData(userDataCache.get(user.email));
        setLoading(false);
        return;
      }

      // Como último recurso, obtener datos del usuario via API
      const userDataFromApi = await getUserByEmail(user.email);

      // Guardar en caché
      userDataCache.set(user.email, userDataFromApi);
      setUserData(userDataFromApi);
      setError(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err instanceof Error && err.message.includes("404")) {
        setError(
          "Tu perfil no ha sido configurado en el sistema. Contacta al administrador.",
        );
      } else {
        setError("Error al cargar los datos del usuario");
      }
    } finally {
      setLoading(false);
    }
  }, [user?.email, getUserByEmail, hasAttemptedFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDataUpdated = useCallback(async () => {
    // Limpiar caché y volver a cargar
    if (user?.email) {
      userDataCache.delete(user.email);
      clearUserCache(user.email); // Also clear the global useUsersApi cache
    }
    setHasAttemptedFetch(false);
    await fetchData();

    // También actualizar el auth context para que los cambios se reflejen en otros componentes
    await refreshUserData();
  }, [fetchData, user?.email, refreshUserData, clearUserCache]);

  // Función para obtener los nombres de las marcas del usuario
  const getUserBrandNames = (user: any) => {
    if (user.brands && Array.isArray(user.brands)) {
      return user.brands.map((brand: any) => brand.name);
    }

    if (user.brand_ids && Array.isArray(user.brand_ids)) {
      if (brands.length === 0) return [];
      return user.brand_ids
        .map((brandId: number) => brands.find((b) => b.id === brandId)?.name)
        .filter(Boolean) as string[];
    }

    return [];
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!user || !userData) {
    return <div>No se pudieron cargar los datos del usuario</div>;
  }

  // Agregar las marcas al userData antes de pasarlo al componente
  const userDataWithBrands = {
    ...userData,
    brandNames: getUserBrandNames(userData),
  };

  // Ensure ProfileInfoSection receives the correct userData for input values
  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        title="Mi perfil"
        description="Ajustes y preferencias del perfil"
      />
      <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto mt-0">
        <ProfileInfoSection
          userData={userDataWithBrands}
          user={user}
          onDataUpdated={handleDataUpdated}
          // Pass a key to force re-mount when userData changes, ensuring input values update
          key={userDataWithBrands?.id || userDataWithBrands?.email}
        />

        <PasswordSection userData={userDataWithBrands} />
      </div>
    </div>
  );
}
